import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { EdpModalService } from "@edp-components/edp-modal";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { ExternalLinkService } from "@edp-directives/external-link/external-link.service";
import { environment } from "@edp-environments/environment";
import { BenefitsRoute } from "@edp-models/benefits-route";
import { AppliedConditions, ICard, Options } from "@edp-models/benefits.model";
import { IDevice } from "@edp-models/device.model";
import { EdpSelectList } from "@edp-models/edp-select-list";
import { ApiService } from "@edp-services/api.service";
import { BenefitsService } from "@edp-services/benefits.service";
import { ActiveShareModal, GetAvailable, ReloadFilters } from "@edp-store/actions/benefits.actions";
import { CopyClipboardService } from "@edp-services/copy-clipboard.service";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { GetDashboardSections } from "@edp-store/actions/edpe.actions";
import { ActiveItemSidebarAction } from "@edp-store/actions/layout.actions";
import { GetRatingStatus } from "@edp-store/actions/rating.actions";
import { getMultiCodesPartnerCards } from "@edp-store/selectors/benefits.selectors";
import { getDevice } from "@edp-store/selectors/notifications.selectors";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { EmailValidator } from "@edp-validators/email.validator";
import { PhoneValidator } from "@edp-validators/phone.validator";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { of, Subject } from "rxjs";
import { catchError, switchMap, takeUntil } from "rxjs/operators";
import { MultiCode } from "./code-generation.model";
import { DatalayerService } from "@edp-services/datalayer.service";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

export enum ButtonStatus {
    success = "success",
    initialState = "initialState",
    error = "error",
}

@Component({
    selector: "edp-e-code-generation-modal",
    templateUrl: "./edp-e-code-generation-modal.component.html",
    styleUrls: ["./edp-e-code-generation-modal.component.scss"],
})
export class EdpECodeGenerationModalComponent implements OnInit, OnDestroy {
    @Input() public data: IModalData;

    public card: ICard;
    public canGenerateCode: boolean;
    public wdgHeight: string;
    public codeGenerated: boolean;
    public emailFilledWithSuccess: boolean;
    public phoneFilledWithSuccess: boolean;
    public errorGenerated: boolean;
    public sucessForm: FormGroup;
    public emailSent: boolean;
    public smsSent: boolean;
    public emailButtonMsg: string = this.translate.instant("pt.edp.edponline.widget.code.generation.modal.sendEmail");
    public smsButtonMsg: string = this.translate.instant("pt.edp.edponline.widget.code.generation.modal.sendSMS");
    public userEmail: any;
    public userPhone: any;
    public edpList: EdpSelectList[] = [];
    public labelDescription: string;
    public partnerSelected: ICard;
    public optionSelected: Options;
    public code: string;
    public codeValidation: string;
    public showPhoneError: boolean;
    public showEmailError: boolean;
    public emailButtonStatus: ButtonStatus = ButtonStatus.initialState;
    public smsButtonStatus: ButtonStatus = ButtonStatus.initialState;
    public dropdownFormControl: FormGroup;
    public emptyEmail: boolean;
    public emptyPhone: boolean;
    public isApp: boolean;
    public hash: string;
    public benefits: BenefitsRoute;
    public multiCodePartner: boolean;
    public multiCodes: MultiCode[] = [];
    public allMultiCodesGenerated = false;
    public multiCodesLoaded = true;
    public appliedConditions: AppliedConditions;
    private readonly unsubscribe = new Subject();
    private conditionTermsChecked: boolean;
    private benefitSelected: boolean;
    public showCopyCode = true;
    public afterGenerate = false;

    constructor(
        private readonly fb: FormBuilder,
        private readonly edpModalService: EdpModalService,
        private readonly benefitsService: BenefitsService,
        private readonly store: Store<IAppState>,
        private readonly translate: TranslateService,
        private readonly externalLinkService: ExternalLinkService,
        private readonly googleAnalyticsService: GoogleAnalyticsService,
        private readonly apiService: ApiService,
        private readonly copyClipboard: CopyClipboardService,
        private readonly datalayerService: DatalayerService,
        private elementRef: ElementRef,
        private sanitizer: DomSanitizer
    ) {
        this.store.select(getUser).subscribe(user => {
            this.userEmail = user.email;
            this.userPhone = user.phone;
        });

        this.benefits = {
            route: "/beneficios",
            param: { Type: "Ofertas em parceiro" },
            activeSidebar: "beneficios",
        };
    }

    ngOnInit(): void {
        this.generateTokenBenefits().subscribe(resp => {
            if (resp instanceof Error) {
                this.errorGenerated = true;
            } else {
                this.hash = resp.Result.Collection;
            }
        });
        // update size of modal according to type
        this.card = this.data.collection.card;

        if (typeof this.data.collection.card.options === "string") {
            this.card = { ...this.card, options: JSON.parse(this.data.collection.card.options) };
        }

        this.store.pipe(select(getMultiCodesPartnerCards), takeUntil(this.unsubscribe)).subscribe((cards: any) => {
            if (cards) {
                cards.forEach(card => {
                    if (card.id === this.card.id) {
                        this.multiCodePartner = true;
                        this.multiCodesLoaded = false;
                        if (!this.appliedConditions) {
                            this.getPartnerConditions(card);
                        } else {
                            this.setMultiCodePartnerData(card);
                        }
                    }
                });
            }
        });

        this.wdgHeight =
            (this.card.options && this.card.options.length > 0) ||
            (this.card.partners && this.card.partners.length > 0) ||
            this.multiCodePartner
                ? "--h33"
                : "--h27";

        if (this.card.options && this.card.options.length > 0) {
            this.card.options.forEach(option => {
                this.edpList.push({
                    text: option.name,
                    value: option.id.toString(),
                });
            });
        } else if (this.card.partners && this.card.partners.length > 0) {
            this.card.partners.forEach(partner => {
                this.edpList.push({
                    text: partner.name,
                    value: partner.id.toString(),
                });
            });
        }

        this.sucessForm = this.fb.group({
            phoneNumber: [this.userPhone, [PhoneValidator(true)]],
            email: [this.userEmail, [EmailValidator]],
        });

        this.dropdownFormControl = this.fb.group({
            dropdown: null,
        });

        this.validateEmail();
        this.validatePhoneNumber();

        if (this.data.collection.card.generatedCode && !this.multiCodePartner) {
            this.code = this.data.collection.card.generatedCode.code;
            this.codeValidation = this.data.collection.card.generatedCode.validationDate;
            this.codeGenerated = true;
            this.errorGenerated = false;
            this.wdgHeight = "--h33";
        }

        this.isApp = environment.isApp;

        if (environment.isApp) {
            this.store.pipe(select(getDevice)).subscribe((device: IDevice) => {
                if (!device) {
                    return;
                }
                const appVersion = Number(device.AppVersion.replace(/[^0-9|]/g, ""));
                const osType = device.OsType.toLowerCase();
                if (osType === "ios" && appVersion < 6230) {
                    this.showCopyCode = false;
                    return;
                }
            });
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public sendEmail(): void {
        if (!this.emailSent) {
            this.emailSent = true;
            this.emailButtonStatus = ButtonStatus.success;
            this.emailButtonMsg = this.translate.instant("pt.edp.edponline.widget.code.generation.modal.emailSent");

            this.datalayerService.setGaClick("clickNavigation", {
                formType: "Beneficio",
                interactionName: "enviar-email",
                interactionLocation: `modal-${this.card.name.toLowerCase().replace(" ", "-")}`,
            });

            this.store.dispatch(new GetRatingStatus());
        }
    }

    public sendSMS(): void {
        if (!this.smsSent) {
            this.smsSent = true;
            this.smsButtonStatus = ButtonStatus.success;
            this.smsButtonMsg = this.translate.instant("pt.edp.edponline.widget.code.generation.modal.smsSent");

            this.datalayerService.setGaClick("clickNavigation", {
                formType: "Beneficio",
                interactionName: "enviar-sms",
                interactionLocation: `modal-${this.card.name.toLowerCase().replace(" ", "-")}`,
            });

            this.store.dispatch(new GetRatingStatus());
        }
    }

    public sendCodes(email: boolean): void {
        this.validateEmail();
        this.validatePhoneNumber();

        let partner: string;
        let name: string;
        let type: string;
        let cardType: string;

        if (this.partnerSelected) {
            partner = this.partnerSelected.id.toString();
            name = this.partnerSelected.name;
            type = this.partnerSelected.type.value.toString();
            cardType = this.partnerSelected.cardTypeId.toString();
        } else {
            partner = this.card.id.toString();
            name = this.card.name;
            type = this.card.type.toString();
            cardType = this.card.cardTypeId.toString();
        }

        if ((email && this.emailFilledWithSuccess) || (!email && this.phoneFilledWithSuccess)) {
            if (email) {
                this.sendEmail();
            } else {
                this.sendSMS();
            }
            this.benefitsService
                .sendCode(
                    this.code,
                    partner,
                    email ? this.sucessForm.value.email : null,
                    !email ? this.sucessForm.value.phoneNumber : null,
                    name,
                    type,
                    cardType
                )
                .pipe(
                    switchMap(resp => of(resp)),
                    catchError(error => of(error))
                )
                .subscribe(() => this.googleAnalyticsService.datalayerEvent("BENEFITS_CODE_SHARED"));
        }
    }

    public validateEmail(): void {
        if (this.sucessForm.controls.email.invalid) {
            this.showEmailError = true;
            this.emailFilledWithSuccess = false;
            this.emptyEmail = true;
        } else {
            if (this.sucessForm.controls.email.value !== "") {
                this.emailFilledWithSuccess = true;
                this.showEmailError = false;
                this.emptyEmail = false;
            } else {
                this.emptyEmail = true;
            }
        }
    }

    public validatePhoneNumber(): void {
        if (this.sucessForm.controls.phoneNumber.invalid) {
            this.showPhoneError = true;
            this.phoneFilledWithSuccess = false;
            this.emptyPhone = true;
        } else {
            if (this.sucessForm.controls.phoneNumber.value) {
                this.phoneFilledWithSuccess = true;
                this.showPhoneError = false;
                this.emptyPhone = false;
            } else {
                this.emptyPhone = true;
            }
        }
    }

    private datalayerGetCode(): void {
        this.datalayerService.setGaClick("clickNavigation", {
            formModal: "Beneficio",
            interactionName: "gerar-codigo",
            interactionLocation: `modal-${this.card.name.toLowerCase().replace(" ", "-")}`,
        });
    }

    private datalayerGetCodeError(): void {
        this.datalayerService.setGaEvent("benefitsCardModalClose", {
            formModal: `modal-${this.card.name.toLowerCase().replace(" ", "-")}`,
            serviceAttributes: {
                mensagemResultado: this.translate.instant("pt.edp.edponline.widget.code.generation.modal.errorGeneratingCode"),
            },
        });
    }

    private datalayerGetCodeClose(): void {
        this.datalayerService.setGaEvent("benefitsCardModalClose", {
            formModal: `modal-${this.card.name.toLowerCase().replace(" ", "-")}`,
        });
    }

    public getCode(): void {
        this.canGenerateCode = false;
        this.multiCodesLoaded = !this.multiCodePartner;
        let partner: string;
        let option: string;

        if (this.partnerSelected) {
            partner = this.partnerSelected.id.toString();
            option = null;
        } else if (this.optionSelected) {
            partner = this.card.id.toString();
            option = this.optionSelected.id.toString();
        } else {
            partner = this.card.id.toString();
            option = null;
        }

        this.datalayerGetCode();

        this.benefitsService
            .getCode(partner, option, this.hash)
            .pipe(
                switchMap(resp => of(resp)),
                catchError(error => of(error))
            )
            .subscribe(resp => {
                if (resp instanceof Error) {
                    this.errorGenerated = true;
                    this.datalayerGetCodeError();
                } else {
                    this.datalayerGetCodeClose();

                    if (!this.multiCodePartner) {
                        this.code = resp.code;
                        this.codeValidation = resp.validity_date;
                        this.codeGenerated = true;
                        this.data.action = "share";
                        this.errorGenerated = false;
                        this.wdgHeight = "--h33";
                    }
                    this.store.dispatch(new ReloadFilters(true));
                    this.store.dispatch(new GetAvailable());
                    this.store.dispatch(new GetDashboardSections());
                    this.googleAnalyticsService.datalayerEvent("BENEFITS_CODE_GENERATION");
                }
            });
    }

    public onSelectListChange(value: string): void {
        this.benefitSelected = true;
        if (this.card.partners && this.card.partners.length > 0) {
            this.card.partners.forEach(partner => {
                if (partner.id === parseInt(value, 0)) {
                    this.labelDescription = partner.description;
                    this.partnerSelected = partner;
                }
            });
        } else if (this.card.options && this.card.options.length > 0) {
            this.card.options.forEach(option => {
                if (option.id === parseInt(value, 0)) {
                    this.optionSelected = option;
                }
            });
        }
        this.verify();
    }

    private datalayerTermsModal(name: string, termsAndConditions: string): void {
        const datalayer = {
            formType: "Beneficios",
            formModal: termsAndConditions,
            interactionName: "termos-condicoes",
            interactionLocation: `modal-${name.toLowerCase().replace(" ", "-")}`,
        };
        this.datalayerService.setGaClick("clickOpenLink", datalayer);
    }

    public openTermsModal(e: Event): void {
        e.preventDefault();
        if (this.card.partners && this.card.partners.length > 0) {
            this.datalayerTermsModal(this.partnerSelected.name, this.partnerSelected.termsAndConditions);

            this.externalLinkService.externalLink(this.partnerSelected.termsAndConditions);
            return;
        }

        this.datalayerTermsModal(this.card.name, this.card.termsAndConditions);
        this.externalLinkService.externalLink(this.card.termsAndConditions);
    }

    public getPartnerName(): string {
        if (this.card.partners && this.card.partners.length > 0) {
            return this.partnerSelected.name;
        } else {
            return this.card.name;
        }
    }

    public onClick(event: any): void {
        this.conditionTermsChecked = event.target.checked;
        this.verify();
    }

    public closeModal(action: boolean): void {
        this.edpModalService.close(action);
    }

    public dismissModal(action: boolean): void {
        this.edpModalService.dismiss(action);
    }

    public onClickLink(benefits: BenefitsRoute): void {
        this.datalayerService.setGaClick("clickNavigation", {
            formType: "Beneficio",
            interactionName: "ver-mais-ofertas",
            interactionLocation: `modal-${this.getPartnerName()
                .toLowerCase()
                .replace(" ", "-")}`,
        });
        this.store.dispatch(new ActiveItemSidebarAction(benefits.activeSidebar));
        this.closeModal(false);
    }

    public copyCode(copy: string): void {
        this.datalayerService.setGaClick("benefitsCardCopy", {
            interactionLocation: `modal-${this.partnerSelected.name.toLowerCase().replace(" ", "-")}`,
        });

        this.copyClipboard.copyToClipboard(copy);
    }

    private getPartnerConditions(card: ICard): void {
        this.benefitsService
            .getPartnerConditions()
            .pipe(
                switchMap(resp => of(resp)),
                catchError(error => of(error))
            )
            .subscribe(resp => {
                if (resp instanceof Error) {
                    this.errorGenerated = true;
                } else {
                    this.appliedConditions = resp;
                    this.setMultiCodePartnerData(card);
                }
            });
    }

    private setMultiCodePartnerData(card: ICard) {
        const _generatedCodes = card.generatedCodes;
        this.multiCodes = [
            {
                validated: this.appliedConditions.IS_EDP_USER,
                generatedCodes: _generatedCodes && _generatedCodes[0] ? _generatedCodes[0] : null,
            },
            {
                validated:
                    this.appliedConditions.IS_EDP_USER &&
                    (this.appliedConditions.HAS_E_INVOICE || this.appliedConditions.BUSINESS_AGREEMENTS),
                generatedCodes: _generatedCodes && _generatedCodes[1] ? _generatedCodes[1] : null,
            },
            {
                validated:
                    this.appliedConditions.IS_EDP_USER &&
                    this.appliedConditions.HAS_E_INVOICE &&
                    this.appliedConditions.BUSINESS_AGREEMENTS,
                generatedCodes: _generatedCodes && _generatedCodes[2] ? _generatedCodes[2] : null,
            },
        ];
        this.allMultiCodesGenerated = this.multiCodes.every(code => !!code.generatedCodes);
        this.multiCodesLoaded = true;

        if (this.multiCodesLoaded) {
            this.validateElemSelector();
        }
    }

    private verify(): void {
        if ((this.card.options && this.card.options.length > 0) || (this.card.partners && this.card.partners.length > 0)) {
            this.canGenerateCode = this.conditionTermsChecked && this.benefitSelected;
        } else {
            this.canGenerateCode = this.conditionTermsChecked;
        }
        if (this.card.partners && this.card.partners.length > 0 && this.partnerSelected.codesAvailable === 0) {
            this.canGenerateCode = false;
        }
        if (this.multiCodePartner) {
            this.canGenerateCode = false;
            if (this.multiCodes.some(code => code.validated && !code.generatedCodes)) {
                this.canGenerateCode = this.conditionTermsChecked;
            }
        }
    }

    private generateTokenBenefits(args?: any) {
        return this.apiService.callServicePost("partners.generateHBenefits", args);
    }

    private isValidUrl(url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }

    public sanitizeUrl(url: string): SafeUrl {
        if (this.isValidUrl(url)) {
            return this.sanitizer.bypassSecurityTrustUrl(url);
        }
        return this.sanitizer.bypassSecurityTrustUrl("about:blank");
    }

    public getGaClickData(card) {
        return {
            formType: "Beneficio",
            formModal: card.partnerLink,
            interactionName: "saiba-mais",
            interactionLocation: `modal-${card.name.toLowerCase().replace(" ", "-")}`,
        };
    }

    public setHowToClick(card) {
        this.datalayerService.setGaClick("clickOpenLink", {
            formType: "Beneficio",
            formModal: card.howToUseCode,
            interactionName: this.translate.instant("pt.edp.edponline.benefits.howToUse"),
            interactionLocation: `modal-${card.name.toLowerCase().replace(" ", "-")}`,
        });
    }

    private validateElemSelector(): void {
        setTimeout(() => {
            const el: any = this.elementSelector(".redirect");
            if (el) {
                el.addEventListener("click", this.redirect.bind(this));
            }
        }, 500);
    }
    private elementSelector(content: string): any {
        return this.elementRef.nativeElement.querySelector(content);
    }

    private redirect(): void {
        const url = this.translate.instant("pt.edp.edponline.widget.code.generation.modal.multicodes.cta.link");
        this.externalLinkService.externalLink(url);
    }
}
